import { keyframes, styled } from "@mui/material/styles";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { AlertIcon, CloseIcon, ErrorIcon, InfoIcon } from "./icons";
import { focusRing, reducedMotion } from "./tokens";
import { usePresence } from "./usePresence";
const spin = keyframes`to { transform: rotate(360deg); }`;
const SPINNER_SIZE = {
    sm: 12,
    md: 12,
    lg: 16
};
const SpinnerRoot = styled("span", {
    shouldForwardProp: (prop)=>prop !== "px" && prop !== "tone"
})(({ px, tone })=>({
        display: "inline-block",
        flexShrink: 0,
        width: px,
        height: px,
        borderRadius: "50%",
        border: `1.5px solid ${tone === "inverse" ? "rgb(var(--j-on-accent-rgb) / 0.35)" : "var(--j-line)"}`,
        borderTopColor: tone === "inverse" ? "var(--j-on-accent)" : "var(--j-ink-2)",
        animation: `${spin} 800ms linear infinite`,
        [reducedMotion]: {
            animationDuration: "1600ms"
        }
    }));
export function Spinner({ size = "md", tone = "primary", label, className }) {
    return /*#__PURE__*/ React.createElement(SpinnerRoot, {
        px: SPINNER_SIZE[size],
        tone: tone,
        className: className,
        role: label ? "status" : undefined,
        "aria-label": label,
        "aria-hidden": label ? undefined : true
    });
}
const Track = styled("div")({
    height: 4,
    borderRadius: "var(--j-r-xs)",
    background: "var(--j-line-soft)",
    overflow: "hidden"
});
const sweep = keyframes`from { transform: translateX(-100%); } to { transform: translateX(250%); }`;
const Sweep = styled("i")({
    display: "block",
    width: "40%",
    height: "100%",
    borderRadius: "var(--j-r-xs)",
    background: "var(--j-accent)",
    animation: `${sweep} 1.4s var(--j-ease) infinite`,
    [reducedMotion]: {
        animation: "none",
        width: "100%",
        opacity: 0.4
    }
});
const Fill = styled("i")({
    display: "block",
    height: "100%",
    borderRadius: "var(--j-r-xs)",
    background: "var(--j-accent)",
    transition: "transform 400ms var(--j-ease)",
    [reducedMotion]: {
        transition: "none"
    }
});
export function ProgressBar({ value, label, className }) {
    if (value === undefined) {
        return /*#__PURE__*/ React.createElement(Track, {
            className: className,
            role: "progressbar",
            "aria-label": label
        }, /*#__PURE__*/ React.createElement(Sweep, null));
    }
    const clamped = Math.max(0, Math.min(100, value));
    return /*#__PURE__*/ React.createElement(Track, {
        className: className,
        role: "progressbar",
        "aria-label": label,
        "aria-valuemin": 0,
        "aria-valuemax": 100,
        "aria-valuenow": Math.round(clamped)
    }, /*#__PURE__*/ React.createElement(Fill, {
        style: {
            transform: `translateX(${clamped - 100}%)`
        }
    }));
}
const Steps = styled("div")({
    display: "flex",
    gap: 2
});
const Step = styled("i", {
    shouldForwardProp: (prop)=>prop !== "isOn"
})(({ isOn })=>({
        flex: 1,
        height: 4,
        borderRadius: "var(--j-r-xs)",
        background: isOn ? "var(--j-accent)" : "var(--j-line-soft)",
        transition: "background-color var(--j-dur-fade)"
    }));
export function StepsBar({ steps, current, label, className }) {
    return /*#__PURE__*/ React.createElement(Steps, {
        className: className,
        role: "progressbar",
        "aria-label": label,
        "aria-valuemin": 1,
        "aria-valuemax": steps,
        "aria-valuenow": current
    }, Array.from({
        length: steps
    }, (_, index)=>/*#__PURE__*/ React.createElement(Step, {
            key: index,
            isOn: index < current
        })));
}
const STATUS_COLOR = {
    ok: "var(--j-ok)",
    warn: "var(--j-warn)",
    err: "var(--j-err)",
    idle: "var(--j-idle)"
};
const Dot = styled("span", {
    shouldForwardProp: (prop)=>prop !== "status"
})(({ status })=>({
        display: "inline-block",
        width: "0.4375rem",
        height: "0.4375rem",
        borderRadius: "50%",
        flexShrink: 0,
        background: STATUS_COLOR[status]
    }));
export function StatusDot({ status, label, className }) {
    return /*#__PURE__*/ React.createElement(Dot, {
        status: status,
        className: className,
        role: label ? "img" : undefined,
        "aria-label": label,
        "aria-hidden": label ? undefined : true
    });
}
const BANNER_ICON_COLOR = {
    info: "var(--j-info)",
    warn: "var(--j-warn)",
    error: "var(--j-err)"
};
const BANNER_ICON = {
    info: InfoIcon,
    warn: AlertIcon,
    error: ErrorIcon
};
const BannerRoot = styled("div", {
    shouldForwardProp: (prop)=>prop !== "tone"
})(({ tone })=>({
        display: "flex",
        gap: "var(--j-sp-2)",
        alignItems: "flex-start",
        padding: "var(--j-sp-2) var(--j-sp-2) var(--j-sp-2) var(--j-sp-3)",
        border: "1px solid var(--j-line)",
        borderRadius: "var(--j-r-md)",
        background: "var(--j-surface)",
        color: "var(--j-ink)",
        fontSize: "var(--j-fs-md)",
        lineHeight: "var(--j-lh-md)",
        "& > svg": {
            width: 16,
            height: 16,
            marginTop: 1,
            flexShrink: 0,
            color: BANNER_ICON_COLOR[tone]
        },
        "& > .banner-body": {
            flex: 1,
            minWidth: 0
        },
        "& > .banner-action": {
            flexShrink: 0,
            margin: "calc((var(--j-lh-md) - var(--j-btn-h)) / 2) 0"
        },
        "&[data-inline=true]": {
            padding: 0,
            border: 0,
            background: "none",
            color: BANNER_ICON_COLOR[tone]
        }
    }));
export function Banner({ tone = "info", icon, action, children, className, inline = false }) {
    const DefaultIcon = BANNER_ICON[tone];
    return /*#__PURE__*/ React.createElement(BannerRoot, {
        tone: tone,
        className: className,
        "data-inline": inline,
        role: tone === "error" ? "alert" : "status"
    }, icon ?? /*#__PURE__*/ React.createElement(DefaultIcon, {
        size: 16
    }), /*#__PURE__*/ React.createElement("div", {
        className: "banner-body"
    }, children), action && /*#__PURE__*/ React.createElement("div", {
        className: "banner-action"
    }, action));
}
const ToastRoot = styled("div", {
    shouldForwardProp: (prop)=>prop !== "isVisible"
})(({ isVisible })=>({
        position: "fixed",
        left: "var(--j-sp-2)",
        right: "var(--j-sp-2)",
        bottom: "var(--j-sp-2)",
        zIndex: 1400,
        margin: "0 auto",
        maxWidth: "30rem",
        display: "flex",
        alignItems: "center",
        gap: "var(--j-sp-2)",
        padding: "var(--j-sp-2) var(--j-sp-1) var(--j-sp-2) var(--j-sp-3)",
        borderRadius: "var(--j-r-lg)",
        background: "var(--j-surface)",
        color: "var(--j-ink)",
        fontSize: "var(--j-fs-md)",
        lineHeight: "var(--j-lh-md)",
        boxShadow: "var(--j-e3)",
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "none" : "translateY(var(--j-sp-4))",
        transition: "opacity 240ms var(--j-ease), transform 240ms var(--j-ease)",
        [reducedMotion]: {
            transform: "none"
        },
        "& > .toast-message": {
            flex: 1,
            minWidth: 0
        }
    }));
const toastButton = {
    ...focusRing,
    flexShrink: 0,
    height: "var(--j-btn-h)",
    border: 0,
    borderRadius: "var(--j-r-sm)",
    background: "transparent",
    color: "var(--j-ink-2)",
    cursor: "pointer",
    transition: "background-color var(--j-dur-fade), color var(--j-dur-fade)",
    "&:hover": {
        background: "var(--j-hover)",
        color: "var(--j-ink)"
    }
};
const ToastAction = styled("button")({
    ...toastButton,
    padding: "0 var(--j-btn-px)",
    fontSize: "var(--j-fs-md)",
    fontWeight: 500,
    lineHeight: 1,
    whiteSpace: "nowrap"
});
const ToastClose = styled("button")({
    ...toastButton,
    width: "var(--j-btn-h)",
    padding: 0,
    display: "grid",
    placeItems: "center"
});
const DEFAULT_TOAST_MS = 6000;
export function Toast({ open, message, action, tone = "default", onClose, autoHideDuration = DEFAULT_TOAST_MS }) {
    const { t } = useTranslation();
    const { isMounted, isVisible } = usePresence(open);
    const [isPaused, setIsPaused] = useState(false);
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    const shownRef = useRef({
        message,
        action
    });
    if (open && message) shownRef.current = {
        message,
        action
    };
    const shown = shownRef.current;
    useEffect(()=>{
        if (!open || isPaused || autoHideDuration === null) return undefined;
        const timer = setTimeout(()=>onCloseRef.current(), autoHideDuration);
        return ()=>clearTimeout(timer);
    }, [
        open,
        message,
        autoHideDuration,
        isPaused
    ]);
    useEffect(()=>{
        if (!open) setIsPaused(false);
    }, [
        open
    ]);
    if (!isMounted) return null;
    return /*#__PURE__*/ createPortal(/*#__PURE__*/ React.createElement(ToastRoot, {
        isVisible: isVisible,
        role: tone === "error" ? "alert" : "status",
        "aria-live": tone === "error" ? "assertive" : "polite",
        onMouseEnter: ()=>setIsPaused(true),
        onMouseLeave: ()=>setIsPaused(false),
        onFocus: ()=>setIsPaused(true),
        onBlur: ()=>setIsPaused(false)
    }, /*#__PURE__*/ React.createElement(StatusDot, {
        status: tone === "error" ? "err" : "idle"
    }), /*#__PURE__*/ React.createElement("span", {
        className: "toast-message"
    }, shown.message), shown.action && /*#__PURE__*/ React.createElement(ToastAction, {
        disabled: !open,
        onClick: ()=>shown.action?.onClick()
    }, shown.action.label), /*#__PURE__*/ React.createElement(ToastClose, {
        type: "button",
        "aria-label": t("ui.close"),
        onClick: onClose
    }, /*#__PURE__*/ React.createElement(CloseIcon, {
        size: 14
    }))), document.body);
}
